/* ============================================================
   DROP A HINT — Client-side logic
   Place at: /assets/drop-a-hint.js
   ============================================================
   Posts two events to Klaviyo via the public Client API:
     1. "Hint Dropped"              — profile = receiver email
     2. "Hint Dropped Confirmation" — profile = sender email

   Both events carry full product + sender + receiver data so
   Klaviyo Flows can render a branded email to either party.
   ============================================================ */

(function () {
  'use strict';

  // ----- Bail if not on a page with the modal -----
  const modal = document.querySelector('[data-dh-modal]');
  if (!modal) return;

  const cfg = window.DropHintConfig || {};
  const product = window.DropHintProductData || {};

  // ----- Elements -----
  const triggers   = document.querySelectorAll('[data-dh-open]');
  const closers    = modal.querySelectorAll('[data-dh-close]');
  const form       = modal.querySelector('[data-dh-form]');
  const success    = modal.querySelector('[data-dh-success]');
  const errorBox   = modal.querySelector('[data-dh-error]');
  const submitBtn  = modal.querySelector('[data-dh-submit]');
  const sendAnotherBtn = modal.querySelector('[data-dh-send-another]');

  // Live preview targets
  const senderPrev   = modal.querySelector('[data-dh-sender-preview]');
  const receiverPrev = modal.querySelector('[data-dh-receiver-preview]');
  const receiverBtnLabel = modal.querySelector('[data-dh-receiver-button]');
  const successReceiver  = modal.querySelector('[data-dh-success-receiver]');

  // Product preview targets
  const previewImage   = modal.querySelector('[data-dh-product-image]');
  const previewVariant = modal.querySelector('[data-dh-variant-preview]');
  const previewPrice   = modal.querySelector('[data-dh-price-preview]');

  // Hidden product inputs
  const hiddenVariantId    = form.querySelector('[data-dh-variant-id]');
  const hiddenVariantTitle = form.querySelector('[data-dh-variant-title]');
  const hiddenVariantPrice = form.querySelector('[data-dh-variant-price]');
  const hiddenVariantSku   = form.querySelector('[data-dh-variant-sku]');
  const hiddenProductImg   = form.querySelector('[data-dh-product-image-input]');
  const hiddenProductUrl   = form.querySelector('[data-dh-product-url]');

  // Field refs
  const senderName    = form.querySelector('[name="sender_name"]');
  const senderEmail   = form.querySelector('[name="sender_email"]');
  const receiverName  = form.querySelector('[name="receiver_name"]');
  const receiverEmail = form.querySelector('[name="receiver_email"]');
  const messageField  = form.querySelector('[name="message"]');
  const charCount     = form.querySelector('[data-dh-charcount]');
  const anonymous     = form.querySelector('[name="anonymous"]');
  const honeypot      = form.querySelector('[name="company"]');
  const templateChips = form.querySelectorAll('[data-dh-template]');

  // ----- Message templates -----
  const TEMPLATES = {
    subtle: "Hi {name}, I came across this and thought it was beautiful. Just wanted to share — no pressure!",
    direct: "Hi {name}, I've been thinking about this piece and would love to have it. Wanted to put it on your radar.",
    birthday: "Hi {name}, my birthday's coming up and I've had my eye on this. Just a hint!",
    anniversary: "Hi {name}, our anniversary is around the corner — this caught my eye and I haven't stopped thinking about it."
  };

  // ============================================================
  // MODAL CONTROLS
  // ============================================================
  let lastFocusedEl = null;

  function openModal() {
    lastFocusedEl = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('dh-modal-open');
    syncFromCurrentVariant();
    setTimeout(() => {
      const firstInput = form.querySelector('input:not([type="hidden"])');
      if (firstInput) firstInput.focus();
    }, 100);
    document.addEventListener('keydown', onEscape);
  }

  function closeModal() {
    modal.hidden = true;
    document.body.classList.remove('dh-modal-open');
    document.removeEventListener('keydown', onEscape);
    if (lastFocusedEl) lastFocusedEl.focus();
  }

  function onEscape(e) {
    if (e.key === 'Escape') closeModal();
  }

  triggers.forEach(t => t.addEventListener('click', openModal));
  closers.forEach(c => c.addEventListener('click', closeModal));

  // ============================================================
  // VARIANT TRACKING
  // ============================================================
  // Re-snapshot the product info when the modal opens, since the
  // user may have changed size/metal between page load and click.

  function getCurrentVariantId() {
    // 1) URL ?variant=
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('variant');
    if (fromUrl) return parseInt(fromUrl, 10);

    // 2) Product form input
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const idInput = productForm.querySelector('[name="id"]');
      if (idInput && idInput.value) return parseInt(idInput.value, 10);
    }

    // 3) Fallback: snippet's default
    return product.variantId;
  }

  function syncFromCurrentVariant() {
    if (!Array.isArray(product.variants)) {
      // We don't have full variant data — just keep snippet defaults
      return;
    }
    // (Variants array isn't always populated; this is a graceful fallback.)
  }

  // Listen for variant changes that happen *while the modal is open*
  // (rare but cheap to support)
  document.addEventListener('variant:change', (e) => {
    const v = e.detail && (e.detail.variant || e.detail);
    if (!v) return;
    applyVariant(v);
  });

  function applyVariant(v) {
    if (!v) return;
    if (v.id) hiddenVariantId.value = v.id;
    if (v.title) {
      hiddenVariantTitle.value = v.title;
      if (previewVariant) previewVariant.textContent = v.title;
    }
    if (v.price) {
      const formatted = formatMoney(v.price);
      hiddenVariantPrice.value = formatted;
      if (previewPrice) previewPrice.textContent = formatted;
    }
    if (v.featured_image && v.featured_image.src) {
      hiddenProductImg.value = v.featured_image.src;
      if (previewImage) previewImage.src = v.featured_image.src;
    }
    if (v.sku) hiddenVariantSku.value = v.sku;
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') return cents;
    try {
      return '$' + (cents / 100).toFixed(2);
    } catch (_) { return ''; }
  }

  // ============================================================
  // LIVE PREVIEW (sender / receiver names)
  // ============================================================
  function updatePreview() {
    const sName = (senderName.value || '').trim();
    const rName = (receiverName.value || '').trim();
    const isAnon = anonymous && anonymous.checked;

    if (senderPrev) {
      senderPrev.textContent = isAnon ? 'someone' : (sName || 'someone');
    }
    if (receiverPrev) {
      receiverPrev.textContent = rName || '…';
    }
    if (receiverBtnLabel) {
      receiverBtnLabel.textContent = rName || 'them';
    }
  }

  [senderName, receiverName].forEach(el => el.addEventListener('input', updatePreview));
  if (anonymous) anonymous.addEventListener('change', updatePreview);

  // ============================================================
  // MESSAGE TEMPLATES + CHAR COUNT
  // ============================================================
  templateChips.forEach(chip => {
    chip.addEventListener('click', () => {
      const key = chip.dataset.dhTemplate;
      const tpl = TEMPLATES[key] || '';
      const rName = (receiverName.value || '').trim() || 'there';
      messageField.value = tpl.replace('{name}', rName);
      messageField.dispatchEvent(new Event('input'));
      templateChips.forEach(c => c.classList.remove('is-active'));
      chip.classList.add('is-active');
      messageField.focus();
    });
  });

  messageField.addEventListener('input', () => {
    if (charCount) charCount.textContent = messageField.value.length;
  });

  // ============================================================
  // VALIDATION
  // ============================================================
  function validate() {
    const errors = [];

    if (!senderName.value.trim()) errors.push('Please enter your name.');
    if (!isValidEmail(senderEmail.value)) errors.push('Please enter a valid email for yourself.');
    if (!receiverName.value.trim()) errors.push("Please enter the receiver's name.");
    if (!isValidEmail(receiverEmail.value)) errors.push("Please enter a valid email for the receiver.");

    if (senderEmail.value.trim().toLowerCase() === receiverEmail.value.trim().toLowerCase()) {
      errors.push("Sender and receiver emails can't be the same.");
    }

    return errors;
  }

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
  }

  function showError(messages) {
    errorBox.hidden = false;
    errorBox.innerHTML = messages.map(m => '<div>' + escapeHtml(m) + '</div>').join('');
  }

  function clearError() {
    errorBox.hidden = true;
    errorBox.innerHTML = '';
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ============================================================
  // SUBMIT
  // ============================================================
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearError();

    // Honeypot — silently drop bot submissions
    if (honeypot && honeypot.value) {
      console.warn('[DropHint] Honeypot triggered, dropping submission.');
      showSuccessUI(); // fake success so bots don't retry
      return;
    }

    const errors = validate();
    if (errors.length) {
      showError(errors);
      return;
    }

    if (!cfg.klaviyoPublicKey || cfg.klaviyoPublicKey === 'YOUR_KLAVIYO_PUBLIC_KEY') {
      showError(['Klaviyo is not configured. Please set your Klaviyo Public API Key.']);
      return;
    }

    setLoading(true);

    // Build the shared event properties
    const fd = new FormData(form);
    const isAnon = !!fd.get('anonymous');
    const variantId = getCurrentVariantId();
    const productUrlWithVariant = appendVariantToUrl(fd.get('product_url'), variantId);

    const sharedProps = {
      // Product
      product_id:     fd.get('product_id'),
      product_title:  fd.get('product_title'),
      product_handle: fd.get('product_handle'),
      product_url:    productUrlWithVariant,
      product_image:  fd.get('product_image'),
      product_price:  fd.get('variant_price'),
      variant_id:     String(variantId || fd.get('variant_id')),
      variant_title:  fd.get('variant_title'),
      variant_sku:    fd.get('variant_sku'),

      // Hint details
      sender_name:      fd.get('sender_name'),
      sender_email:     fd.get('sender_email'),
      sender_display:   isAnon ? 'Someone' : fd.get('sender_name'),
      receiver_name:    fd.get('receiver_name'),
      receiver_email:   fd.get('receiver_email'),
      anonymous:        isAnon,
      occasion:         fd.get('occasion') || '',
      send_date:        fd.get('send_date') || '',
      message:          fd.get('message') || '',

      // Context
      shop_name: cfg.shopName,
      hint_url:  productUrlWithVariant
                  + (productUrlWithVariant.includes('?') ? '&' : '?')
                  + 'utm_source=drop_hint&utm_medium=email&utm_campaign=hint_dropped'
    };

    try {
      // Event 1 — to the receiver
      await sendKlaviyoEvent({
        metric: cfg.eventNameToReceiver || 'Hint Dropped',
        profileEmail: sharedProps.receiver_email,
        profileFirstName: sharedProps.receiver_name,
        properties: sharedProps,
        // Don't add receiver to marketing lists — this is transactional
        skipMarketing: true
      });

      // Event 2 — confirmation to the sender
      await sendKlaviyoEvent({
        metric: cfg.eventNameToSender || 'Hint Dropped Confirmation',
        profileEmail: sharedProps.sender_email,
        profileFirstName: sharedProps.sender_name,
        properties: sharedProps,
        skipMarketing: true
      });

      showSuccessUI();
    } catch (err) {
      console.error('[DropHint] Failed:', err);
      setLoading(false);
      showError(['Something went wrong. Please try again in a moment.']);
    }
  });

  function setLoading(isLoading) {
    if (isLoading) {
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
    } else {
      submitBtn.classList.remove('is-loading');
      submitBtn.disabled = false;
    }
  }

  function showSuccessUI() {
    if (successReceiver) successReceiver.textContent = receiverName.value.trim();
    form.hidden = true;
    success.hidden = false;
  }

  if (sendAnotherBtn) {
    sendAnotherBtn.addEventListener('click', () => {
      form.reset();
      success.hidden = true;
      form.hidden = false;
      setLoading(false);
      updatePreview();
      messageField.dispatchEvent(new Event('input'));
      templateChips.forEach(c => c.classList.remove('is-active'));
    });
  }

  function appendVariantToUrl(url, variantId) {
    if (!url || !variantId) return url || '';
    try {
      const u = new URL(url);
      u.searchParams.set('variant', variantId);
      return u.toString();
    } catch (_) {
      const sep = url.includes('?') ? '&' : '?';
      return url + sep + 'variant=' + variantId;
    }
  }

  // ============================================================
  // KLAVIYO CLIENT API
  // ============================================================
  async function sendKlaviyoEvent({ metric, profileEmail, profileFirstName, properties, skipMarketing }) {
    const url = 'https://a.klaviyo.com/client/events/?company_id='
              + encodeURIComponent(cfg.klaviyoPublicKey);

    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          time: new Date().toISOString(),
          metric: {
            data: {
              type: 'metric',
              attributes: { name: metric }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: profileEmail,
                first_name: profileFirstName,
                properties: skipMarketing ? { is_drop_hint_only: true } : {}
              }
            }
          }
        }
      }
    };

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'revision': cfg.klaviyoRevision || '2024-10-15',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok && res.status !== 202) {
      const text = await res.text().catch(() => '');
      throw new Error('Klaviyo error ' + res.status + ': ' + text);
    }
    return true;
  }
})();
